import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, Database, FileText, FolderKanban, FolderOpen, FolderPlus, LayoutGrid, Lock, Network, RefreshCw, Upload, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { kbService, DomainInfo, KBStats, type KBFolderInfo, type ProjectKBInfo } from '@/services/kbService';
import { customFolderService, type CustomFolderWithCount } from '@/services/customFolderService';
import { indexingService, IndexingTaskStatus } from '@/services/indexingService';
import notify from '@/lib/notify';
import { useEffectiveUser, useIsOrganizationAdmin } from '@/stores/authStore';
import { PageLayout } from '@/components/Layout/PageLayout';
import { Button } from '@/components/Common/button';
import { DocumentsExplorer } from '@/components/Documents/DocumentsExplorer';
import { DefaultFolderBadge, PrivateBadge } from '@/components/Documents/KnowledgeVisibility';
import { KnowledgeFolderTree } from '@/components/Documents/KnowledgeFolderTree';
import {
  ancestorFolderPaths,
  buildFolderTree,
  folderContext,
  folderNodeKey,
  folderPathSegments,
} from '@/lib/knowledgeFolders';
import { apiErrorDetail, canManageSharing } from '@/lib/knowledgeSharing';
import { IndexingTasksTable } from '@/components/Indexing/IndexingTasksTable';
import Modal from '@/components/Common/Modal';

export interface DocumentContext {
  type: 'domain' | 'use_case' | 'cadrage' | 'custom_folder' | 'project';
  id: string;
  name: string;
  path: string[];
  /** Subfolder of a domain, use case or solution (`/a/b/`); absent for the item's root. */
  folderPath?: string;
}

/** Subfolders of the selected domain, use case or solution, as listed in the structure. */
const findContextFolders = (domains: DomainInfo[], context: DocumentContext | null): KBFolderInfo[] | undefined => {
  if (!context) return undefined;
  for (const domain of domains) {
    if (context.type === 'domain' && domain.id === context.id) return domain.folders;
    for (const useCase of domain.use_cases ?? []) {
      if (context.type === 'use_case' && useCase.id === context.id) return useCase.folders;
      const cadrage = useCase.cadrages?.find((candidate) => candidate.id === context.id);
      if (context.type === 'cadrage' && cadrage) return cadrage.folders;
    }
  }
  return undefined;
};

export const KnowledgeBasePage: React.FC = () => {
  const { t } = useTranslation();
  const isOrganizationAdmin = useIsOrganizationAdmin();
  const effectiveUser = useEffectiveUser();
  const [isBusinessSectionOpen, setIsBusinessSectionOpen] = useState(true);
  const [isCustomSectionOpen, setIsCustomSectionOpen] = useState(false);
  const [domains, setDomains] = useState<DomainInfo[]>([]);
  const [customFolders, setCustomFolders] = useState<CustomFolderWithCount[]>([]);
  const [sharingFolderId, setSharingFolderId] = useState<string | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [expandedUseCases, setExpandedUseCases] = useState<Set<string>>(new Set());
  const [expandedCadrages, setExpandedCadrages] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedContext, setSelectedContext] = useState<DocumentContext | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [importingKnowledge, setImportingKnowledge] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [stats, setStats] = useState<KBStats>({
    total_documents: 0,
    indexed_documents: 0,
    pending_documents: 0,
    total_contexts: 0,
  });
  const [indexingTasks, setIndexingTasks] = useState<IndexingTaskStatus[]>([]);
  const [indexingStats, setIndexingStats] = useState({
    indexing_count: 0,
    indexed_count: 0,
    failed_count: 0,
  });
  const [loadingTasks, setLoadingTasks] = useState(false);
  const navigate = useNavigate();
  const [kgQuickStats, setKgQuickStats] = useState({
    docs: 0,
    themes: 0,
    entities: 0,
  });

  useEffect(() => {
    loadKBStructure();
    loadKBStats();
    loadKGQuickStats();
    loadIndexingTasks();

    // Poll indexing tasks every 3 seconds
    const interval = setInterval(() => {
      loadIndexingTasks();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const loadKBStructure = async () => {
    try {
      setLoading(true);
      // Custom folders come from their own endpoint: it carries the sharing fields and
      // hides other users' private folders from members.
      const [structure, folders] = await Promise.all([
        kbService.getStructure(),
        customFolderService.list(),
      ]);
      setDomains(structure.business);
      setCustomFolders(folders);
      // By default keep the "metier" tree expanded.
      setExpandedDomains(new Set(structure.business.map((domain) => domain.id)));
    } catch (error) {
      console.error('Error loading KB structure:', error);
      notify.error(t('knowledgeBasePage.notifications.structureLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadKBStats = async () => {
    try {
      const stats = await kbService.getStats();
      setStats(stats);
    } catch (error) {
      console.error('Error loading KB stats:', error);
    }
  };

  const loadIndexingTasks = async () => {
    try {
      setLoadingTasks(true);
      const response = await indexingService.getIndexingTasks(50);
      setIndexingTasks(response.tasks);
      setIndexingStats({
        indexing_count: response.indexing_count,
        indexed_count: response.indexed_count,
        failed_count: response.failed_count,
      });
    } catch (error) {
      console.error('Error loading indexing tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadKGQuickStats = async () => {
    try {
      const graphData = await kbService.getContextGraph();
      const nodes = graphData.nodes || [];
      const docs =
        typeof graphData.statistics?.documents === 'number'
          ? graphData.statistics.documents
          : nodes.filter((node) => node.type === 'Document').length;
      const themes = nodes.filter((node) => node.type === 'Theme').length;
      const entities = nodes.filter((node) => node.type === 'Entity').length;
      setKgQuickStats({ docs, themes, entities });
    } catch (error) {
      console.error('Error loading KG quick stats:', error);
      setKgQuickStats({ docs: 0, themes: 0, entities: 0 });
    }
  };

  const handleRetryFailed = async () => {
    try {
      notify.info(t('knowledgeBasePage.notifications.retryStarted'));
      const result = await indexingService.retryFailedDocuments();
      notify.success(t('knowledgeBasePage.notifications.retrySuccess', { count: result.retried_count }));
      await loadIndexingTasks(); // Refresh the task list
    } catch (error) {
      console.error('Error retrying failed documents:', error);
      notify.error(t('knowledgeBasePage.notifications.retryError'));
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      notify.error(t('knowledgeBasePage.notifications.folderNameRequired'));
      return;
    }

    setCreatingFolder(true);
    try {
      await customFolderService.create({
        name: newFolderName.trim(),
        description: newFolderDescription.trim() || undefined,
      });
      notify.success(t('knowledgeBasePage.notifications.folderCreated'));
      setNewFolderName('');
      setNewFolderDescription('');
      setShowCreateFolderModal(false);
      await loadKBStructure();
      await loadKBStats();
    } catch (error) {
      console.error('Error creating folder:', error);
      const httpError = error as { response?: { status?: number } };
      if (httpError.response?.status === 409) {
        notify.error(t('knowledgeBasePage.notifications.folderDuplicate'));
      } else {
        notify.error(t('knowledgeBasePage.notifications.folderCreateError'));
      }
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleToggleFolderSharing = async (folder: CustomFolderWithCount, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering context selection

    const shared = !folder.shared_with_org;
    setSharingFolderId(folder.id);
    try {
      const updated = await customFolderService.setSharing(folder.id, shared);
      setCustomFolders((prev) =>
        prev.map((f) => (f.id === folder.id ? { ...f, shared_with_org: updated.shared_with_org } : f))
      );
      notify.success(
        updated.shared_with_org ? t('knowledgeSharing.folder.shared') : t('knowledgeSharing.folder.madePrivate')
      );
    } catch (error) {
      console.error('Error changing folder sharing:', error);
      notify.error(apiErrorDetail(error) ?? t('knowledgeSharing.errors.updateFailed'));
    } finally {
      setSharingFolderId(null);
    }
  };

  const handleImportKnowledgeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange.
    event.target.value = '';
    if (!file) return;

    setImportingKnowledge(true);
    try {
      const result = await kbService.importKnowledge(file);
      notify.success(
        t('knowledgeBasePage.import.success', {
          defaultValue: '{{entities}} entité(s) et {{relationships}} relation(s) importée(s)',
          entities: result.entities_imported,
          relationships: result.relationships_imported,
        })
      );
      if (result.warnings.length > 0) {
        console.warn('Knowledge import warnings:', result.warnings);
        notify.warning(
          result.warnings.join('\n'),
          t('knowledgeBasePage.import.warningsTitle', "Avertissements d'import")
        );
      }
      await loadKBStats();
      await loadKGQuickStats();
    } catch (error) {
      console.error('Error importing knowledge:', error);
      const status = (error as { response?: { status?: number } }).response?.status;
      let message: string;
      if (status === 400) {
        message = t('knowledgeBasePage.import.errorEmpty', 'Le fichier est vide');
      } else if (status === 413) {
        message = t(
          'knowledgeBasePage.import.errorTooLarge',
          "Fichier trop volumineux (10 Mo max) ou trop d'entités"
        );
      } else if (status === 422) {
        message = t(
          'knowledgeBasePage.import.errorInvalid',
          'Format ou contenu du fichier invalide (.json, .jsonld, .ttl, .owl, .rdf ou .xml attendu)'
        );
      } else {
        message = t('knowledgeBasePage.import.errorGeneric', "Erreur lors de l'import des connaissances");
      }
      notify.error(message);
    } finally {
      setImportingKnowledge(false);
    }
  };

  const handleRefresh = async () => {
    await loadKBStructure();
    await loadKBStats();
    await loadKGQuickStats();
  };

  const handleIndexContext = async (
    contextType: 'domain' | 'use_case' | 'cadrage' | 'custom_folder',
    contextId: string,
    contextName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent triggering context selection

    try {
      notify.info(t('knowledgeBasePage.notifications.indexingContext', { name: contextName }));
      const result = await indexingService.indexContext(contextType, contextId);
      notify.success(t('knowledgeBasePage.notifications.indexingLaunched', { count: result.document_count }));
      await loadKBStats(); // Refresh stats
    } catch (error) {
      console.error('Error indexing context:', error);
      notify.error(t('knowledgeBasePage.notifications.indexingError'));
    }
  };

  const handleSelectContext = (context: DocumentContext) => {
    setSelectedContext(context);
  };

  const toggleDomain = (domainId: string) => {
    setExpandedDomains(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domainId)) {
        newSet.delete(domainId);
      } else {
        newSet.add(domainId);
      }
      return newSet;
    });
  };

  const toggleUseCase = (useCaseId: string) => {
    setExpandedUseCases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(useCaseId)) {
        newSet.delete(useCaseId);
      } else {
        newSet.add(useCaseId);
      }
      return newSet;
    });
  };

  const toggleCadrage = (cadrageId: string) => {
    setExpandedCadrages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cadrageId)) {
        newSet.delete(cadrageId);
      } else {
        newSet.add(cadrageId);
      }
      return newSet;
    });
  };

  const toggleFolder = (folderKey: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderKey)) {
        newSet.delete(folderKey);
      } else {
        newSet.add(folderKey);
      }
      return newSet;
    });
  };

  // Folder navigation from the explorer: open the folder and unfold the tree down to it.
  const handleOpenFolder = (folderPath: string) => {
    if (!selectedContext) return;
    const rootPath = selectedContext.path.slice(
      0,
      selectedContext.path.length - folderPathSegments(selectedContext.folderPath ?? '/').length
    );
    const rootContext: DocumentContext = {
      type: selectedContext.type,
      id: selectedContext.id,
      name: rootPath[rootPath.length - 1] ?? selectedContext.name,
      path: rootPath,
    };
    setSelectedContext(folderContext(rootContext, folderPath));
    if (rootContext.type === 'cadrage') {
      setExpandedCadrages(prev => new Set(prev).add(rootContext.id));
    }
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      ancestorFolderPaths(folderPath).forEach((path) => newSet.add(folderNodeKey(rootContext, path)));
      return newSet;
    });
  };

  const isRootSelected = (type: DocumentContext['type'], id: string) =>
    selectedContext?.type === type && selectedContext.id === id && !selectedContext.folderPath;

  const selectedContextFolders = findContextFolders(domains, selectedContext);

  const selectedCustomFolder =
    selectedContext?.type === 'custom_folder'
      ? customFolders.find((folder) => folder.id === selectedContext.id)
      : undefined;

  const selectedProject: ProjectKBInfo | undefined =
    selectedContext?.type === 'project'
      ? domains.flatMap((domain) => domain.projects ?? []).find((project) => project.id === selectedContext.id)
      : undefined;

  if (loading) {
    return (
      <PageLayout
        icon={<Database className="h-6 w-6" />}
        title={t('knowledgeBasePage.title')}
        subtitle={t('knowledgeBasePage.loadingSubtitle')}
      >
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mr-3" />
          <p className="text-muted-foreground">{t('knowledgeBasePage.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      icon={<Database className="h-6 w-6" />}
      title={t('knowledgeBasePage.title')}
      subtitle={t('knowledgeBasePage.subtitle')}
      headerRight={
        <div className="flex items-center gap-2">
          {isOrganizationAdmin && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept=".json,.jsonld,.ttl,.owl,.rdf,.xml"
                className="hidden"
                onChange={handleImportKnowledgeFile}
              />
              <Button
                onClick={() => importInputRef.current?.click()}
                variant="outline"
                size="sm"
                disabled={importingKnowledge}
              >
                <Upload className="h-4 w-4 mr-2" />
                {importingKnowledge
                  ? t('knowledgeBasePage.import.importing', 'Import en cours...')
                  : t('knowledgeBasePage.import.button', 'Importer des connaissances')}
              </Button>
            </>
          )}
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('knowledgeBasePage.refresh')}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-card border border-border rounded-xl p-6 w-full">
          <p className="text-sm font-medium text-muted-foreground mb-3">{t('knowledgeBasePage.kpis.documents')}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('knowledgeBasePage.kpis.total')}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                  {t('knowledgeBasePage.kpis.totalBadge', { count: stats.total_documents })}
                </span>
                <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
                  <FileText className="h-4 w-4 text-primary" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('knowledgeBasePage.kpis.indexed')}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300">
                  {t('knowledgeBasePage.kpis.indexedBadge', { count: stats.indexed_documents })}
                </span>
                <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
                  <Database className="h-4 w-4 text-primary" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('knowledgeBasePage.kpis.pending')}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                  {t('knowledgeBasePage.kpis.pendingBadge', { count: stats.pending_documents })}
                </span>
                <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
                  <RefreshCw className="h-4 w-4 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/knowledge-base/graph')}
          className="bg-card border border-border rounded-xl p-6 w-full text-left transition-all hover:border-primary hover:shadow-lg hover:scale-105 cursor-pointer"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {t('knowledgeBasePage.kpis.knowledgeGraph')}
              </p>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                    {t('knowledgeBasePage.kpis.docsBadge', { count: kgQuickStats.docs })}
                  </span>
                </p>
                <p>
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300">
                    {t('knowledgeBasePage.kpis.themesBadge', { count: kgQuickStats.themes })}
                  </span>
                </p>
                <p>
                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                    {t('knowledgeBasePage.kpis.entitiesBadge', { count: kgQuickStats.entities })}
                  </span>
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">{t('knowledgeBasePage.kpis.clickToShow')}</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center ml-3">
              <Network className="h-6 w-6 text-primary" />
            </div>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-[620px]">
          <div className="bg-card border rounded-lg overflow-hidden h-full flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              <div className="border rounded-md">
                <button
                  onClick={() => setIsBusinessSectionOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold hover:bg-accent/50 transition-colors"
                >
                  <span>{t('knowledgeBasePage.tabs.business')}</span>
                  {isBusinessSectionOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {isBusinessSectionOpen && (
                  <div className="px-2 pb-2">
                    <div className="space-y-2">
                      {domains.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p className="text-sm">{t('knowledgeBasePage.empty.noDomainWithDocs')}</p>
                        </div>
                      ) : (
                        domains.map((domain) => (
                          <div key={domain.id} className="space-y-1">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => toggleDomain(domain.id)}
                                className="p-1 hover:bg-accent rounded"
                              >
                                {expandedDomains.has(domain.id) ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </button>
                              <div
                                onClick={() => handleSelectContext({
                                  type: 'domain',
                                  id: domain.id,
                                  name: domain.name,
                                  path: [domain.name]
                                })}
                                className={`
                                  flex-1 flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                                  hover:bg-accent
                                  ${isRootSelected('domain', domain.id)
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : ''
                                  }
                                `}
                              >
                                <LayoutGrid className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                <span
                                  className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[200px]"
                                  title={domain.name}
                                >
                                  {domain.name}
                                </span>
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0">
                                  {domain.document_count || 0}
                                </span>
                                <button
                                  onClick={(e) => handleIndexContext('domain', domain.id, domain.name, e)}
                                  className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0"
                                  title={t('knowledgeBasePage.actions.indexDomain')}
                                >
                                  <RefreshCw className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                </button>
                              </div>
                            </div>

                            {expandedDomains.has(domain.id) && (
                              <KnowledgeFolderTree
                                nodes={buildFolderTree(domain.folders)}
                                context={{ type: 'domain', id: domain.id, name: domain.name, path: [domain.name] }}
                                selectedContext={selectedContext}
                                expandedFolders={expandedFolders}
                                onToggle={toggleFolder}
                                onSelect={handleSelectContext}
                              />
                            )}

                            {expandedDomains.has(domain.id) && domain.use_cases?.map((useCase) => (
                              <div key={useCase.id} className="ml-5 space-y-1">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => toggleUseCase(useCase.id)}
                                    className="p-1 hover:bg-accent rounded"
                                  >
                                    {expandedUseCases.has(useCase.id) ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </button>
                                  <div
                                    onClick={() => handleSelectContext({
                                      type: 'use_case',
                                      id: useCase.id,
                                      name: useCase.name,
                                      path: [domain.name, useCase.name]
                                    })}
                                    className={`
                                      flex-1 flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                                      hover:bg-accent
                                      ${isRootSelected('use_case', useCase.id)
                                        ? 'bg-primary/10 text-primary font-medium'
                                        : ''
                                      }
                                    `}
                                  >
                                    <ClipboardList className="h-4 w-4 text-green-500 flex-shrink-0" />
                                    <span
                                      className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[180px]"
                                      title={useCase.name}
                                    >
                                      {useCase.name}
                                    </span>
                                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0">
                                      {useCase.document_count || 0}
                                    </span>
                                    <button
                                      onClick={(e) => handleIndexContext('use_case', useCase.id, useCase.name, e)}
                                      className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0"
                                      title={t('knowledgeBasePage.actions.indexUseCase')}
                                    >
                                      <RefreshCw className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                    </button>
                                  </div>
                                </div>

                                {expandedUseCases.has(useCase.id) && (
                                  <KnowledgeFolderTree
                                    nodes={buildFolderTree(useCase.folders)}
                                    context={{
                                      type: 'use_case',
                                      id: useCase.id,
                                      name: useCase.name,
                                      path: [domain.name, useCase.name],
                                    }}
                                    selectedContext={selectedContext}
                                    expandedFolders={expandedFolders}
                                    onToggle={toggleFolder}
                                    onSelect={handleSelectContext}
                                  />
                                )}

                                {expandedUseCases.has(useCase.id) && useCase.cadrages?.map((cadrage) => {
                                  const cadrageFolders = buildFolderTree(cadrage.folders);
                                  const cadrageContext: DocumentContext = {
                                    type: 'cadrage',
                                    id: cadrage.id,
                                    name: cadrage.name,
                                    path: [domain.name, useCase.name, cadrage.name],
                                  };

                                  return (
                                    <div key={cadrage.id} className="ml-5 space-y-1">
                                      <div className="flex items-center gap-1">
                                        {cadrageFolders.length > 0 ? (
                                          <button
                                            onClick={() => toggleCadrage(cadrage.id)}
                                            className="p-1 hover:bg-accent rounded"
                                          >
                                            {expandedCadrages.has(cadrage.id) ? (
                                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            )}
                                          </button>
                                        ) : (
                                          <span className="w-6 flex-shrink-0" />
                                        )}
                                        <div
                                          onClick={() => handleSelectContext(cadrageContext)}
                                          className={`
                                            flex-1 flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                                            hover:bg-accent
                                            ${isRootSelected('cadrage', cadrage.id)
                                              ? 'bg-primary/10 text-primary font-medium'
                                              : ''
                                            }
                                          `}
                                        >
                                          <FileText className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                          <span
                                            className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[160px]"
                                            title={cadrage.name}
                                          >
                                            {cadrage.name}
                                          </span>
                                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0">
                                            {cadrage.document_count || 0}
                                          </span>
                                          <button
                                            onClick={(e) => handleIndexContext('cadrage', cadrage.id, cadrage.name, e)}
                                            className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0"
                                            title={t('knowledgeBasePage.actions.indexCadrage')}
                                          >
                                            <RefreshCw className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </div>
                                      </div>

                                      {expandedCadrages.has(cadrage.id) && (
                                        <KnowledgeFolderTree
                                          nodes={cadrageFolders}
                                          context={cadrageContext}
                                          selectedContext={selectedContext}
                                          expandedFolders={expandedFolders}
                                          onToggle={toggleFolder}
                                          onSelect={handleSelectContext}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ))}

                            {expandedDomains.has(domain.id) && domain.projects?.map((project) => (
                              <div
                                key={project.id}
                                onClick={() => handleSelectContext({
                                  type: 'project',
                                  id: project.id,
                                  name: project.name,
                                  path: [domain.name, project.name]
                                })}
                                className={`
                                  ml-5 flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                                  hover:bg-accent
                                  ${selectedContext?.id === project.id && selectedContext?.type === 'project'
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : ''
                                  }
                                `}
                              >
                                <FolderKanban className="h-4 w-4 text-violet-500 flex-shrink-0" />
                                <span
                                  className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[180px]"
                                  title={project.name}
                                >
                                  {project.name}
                                </span>
                                {project.is_private && (
                                  <PrivateBadge tooltip={t('knowledgeBasePage.projects.privateTooltip')} />
                                )}
                                <span
                                  className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0"
                                  title={t('knowledgeBasePage.projects.indexedCount', {
                                    indexed: project.indexed_count,
                                    total: project.document_count,
                                  })}
                                >
                                  {project.indexed_count}/{project.document_count}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="border rounded-md">
                <button
                  onClick={() => setIsCustomSectionOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold hover:bg-accent/50 transition-colors"
                >
                  <span>{t('knowledgeBasePage.tabs.custom')}</span>
                  {isCustomSectionOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {isCustomSectionOpen && (
                  <div className="px-2 pb-2 space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-sm">{t('knowledgeBasePage.customFolders.title')}</h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowCreateFolderModal(true)}
                      >
                        <FolderPlus className="h-4 w-4" />
                      </Button>
                    </div>

                    {customFolders.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FolderOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{t('knowledgeBasePage.customFolders.emptyTitle')}</p>
                        <p className="text-xs mt-1">{t('knowledgeBasePage.customFolders.emptyDescription')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {customFolders.map((folder) => {
                          const isSystemFolder = !!folder.system_key;
                          // Default folders are always shared: no sharing toggle on them.
                          const canToggleSharing =
                            !isSystemFolder && canManageSharing(effectiveUser, isOrganizationAdmin, folder.owner_id);
                          const sharingLabel = folder.shared_with_org
                            ? t('knowledgeSharing.folder.makePrivate')
                            : t('knowledgeSharing.folder.share');

                          return (
                            <div
                              key={folder.id}
                              onClick={() => handleSelectContext({
                                type: 'custom_folder',
                                id: folder.id,
                                name: folder.name,
                                path: [t('knowledgeBasePage.tabs.custom'), folder.name]
                              })}
                              className={`
                                flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                                hover:bg-accent
                                ${selectedContext?.id === folder.id && selectedContext?.type === 'custom_folder'
                                  ? 'bg-primary/10 text-primary font-medium'
                                  : ''
                                }
                              `}
                            >
                              <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <span
                                className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[200px]"
                                title={folder.name}
                              >
                                {folder.name}
                              </span>
                              {isSystemFolder ? (
                                <DefaultFolderBadge />
                              ) : (
                                !folder.shared_with_org && (
                                  <PrivateBadge tooltip={t('knowledgeSharing.badges.privateFolderTooltip')} />
                                )
                              )}
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0">
                                {folder.document_count}
                              </span>
                              {canToggleSharing && (
                                <button
                                  onClick={(e) => handleToggleFolderSharing(folder, e)}
                                  disabled={sharingFolderId === folder.id}
                                  className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0 disabled:opacity-50"
                                  title={sharingLabel}
                                  aria-label={sharingLabel}
                                >
                                  {folder.shared_with_org ? (
                                    <Lock className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                  ) : (
                                    <Users className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={(e) => handleIndexContext('custom_folder', folder.id, folder.name, e)}
                                className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0"
                                title={t('knowledgeBasePage.actions.indexCustomFolder')}
                              >
                                <RefreshCw className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="h-[620px]">
          {selectedContext ? (
            <DocumentsExplorer
              context={selectedContext}
              customFolder={selectedCustomFolder}
              project={selectedProject}
              folders={selectedContextFolders}
              onOpenFolder={handleOpenFolder}
              onDocumentsChange={loadKBStats}
            />
          ) : (
            <div className="bg-card border rounded-lg p-12 h-full flex flex-col items-center justify-center text-center">
              <Database className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t('knowledgeBasePage.empty.selectContextTitle')}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {t('knowledgeBasePage.empty.selectContextDescription')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Indexing Tasks Table */}
      {indexingTasks.length > 0 && (
        <div className="mt-6">
          <IndexingTasksTable
            tasks={indexingTasks}
            indexingCount={indexingStats.indexing_count}
            indexedCount={indexingStats.indexed_count}
            failedCount={indexingStats.failed_count}
            onRetryFailed={handleRetryFailed}
            isLoading={loadingTasks}
          />
        </div>
      )}

      {/* Create Custom Folder Modal */}
      <Modal
        open={showCreateFolderModal}
        title={t('knowledgeBasePage.modal.createFolderTitle')}
        onClose={() => {
          setShowCreateFolderModal(false);
          setNewFolderName('');
          setNewFolderDescription('');
        }}
        hideDefaultFooter
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="folderName" className="block text-sm font-medium mb-2">
              {t('knowledgeBasePage.modal.folderNameLabel')} <span className="text-destructive">*</span>
            </label>
            <input
              id="folderName"
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('knowledgeBasePage.modal.folderNamePlaceholder')}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={creatingFolder}
            />
          </div>

          <div>
            <label htmlFor="folderDescription" className="block text-sm font-medium mb-2">
              {t('knowledgeBasePage.modal.folderDescriptionLabel')}
            </label>
            <textarea
              id="folderDescription"
              value={newFolderDescription}
              onChange={(e) => setNewFolderDescription(e.target.value)}
              placeholder={t('knowledgeBasePage.modal.folderDescriptionPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              disabled={creatingFolder}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateFolderModal(false);
                setNewFolderName('');
                setNewFolderDescription('');
              }}
              disabled={creatingFolder}
            >
              {t('knowledgeBasePage.modal.cancel')}
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
            >
              {creatingFolder ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('knowledgeBasePage.modal.creating')}
                </>
              ) : (
                <>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  {t('knowledgeBasePage.modal.create')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
};
